import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { Table, Spin, Alert } from 'antd';
import styled from 'styled-components';
import CommandsGridToolbar from './CommandsGridToolbar';
import { formatTotal } from '../eventViewer.constants';

const Container = styled.div`
	.ant-table td,
	.ant-table th {
		white-space: normal;
		word-wrap: break-word;
		word-break: break-word;
		overflow-wrap: break-word;
	}
`;

const formatTs = (ts) => ts ? moment(ts).format('D-MMM-YYYY HH:mm:ss') : '';

const loadData = (grid, fetchCommands) => {
	const filters = Object.keys(grid.params.filters || {}).reduce(
		(accumulator, currentKey) => {
			const currentValue = grid.params.filters[currentKey];
			accumulator[currentKey] = Array.isArray(currentValue)
				? currentValue.join()
				: currentValue;
			return accumulator;
		},
		{}
	);
	// Never query without a device serial number (same rule as EventsGrid): an
	// unfiltered commands search is a COUNT(*) over the whole Commands table per page.
	if (!filters.deviceSerialNumber) return;
	const params = {
		...grid.params,
		filters,
	};
	fetchCommands(params);
};

const hasDeviceSerial = (grid) => !!(grid && grid.params && grid.params.filters && grid.params.filters.deviceSerialNumber);

const columns = [
	{
		key: 'deviceType',
		title: 'Device Type',
		dataIndex: 'deviceType',
		sorter: true,
		width: 150,
	},
	{
		key: 'deviceSerialNumber',
		title: 'Device Serial Number',
		dataIndex: 'deviceSerialNumber',
		sorter: true,
		width: 180,
	},
	{
		key: 'commandName',
		title: 'Command Name',
		dataIndex: 'commandName',
		sorter: true,
		width: 150,
	},
	{
		key: 'entryKey',
		title: 'Entry Key',
		dataIndex: 'entryKey',
		sorter: true,
		width: 150,
	},
	{
		key: 'entryValue',
		title: 'Entry Value',
		dataIndex: 'entryValue',
		width: 150,
	},
	{
		key: 'sessionId',
		title: 'Session ID',
		dataIndex: 'sessionId',
		width: 150,
	},
	{
		key: 'timestamp',
		title: 'Timestamp',
		dataIndex: 'timestamp',
		sorter: true,
		width: 180,
		render: formatTs,
	},
	{
		key: 'localEntryTimestamp',
		title: 'Local Entry Timestamp',
		dataIndex: 'localEntryTimestamp',
		width: 180,
		render: formatTs,
	},
];

const serializeParams = (params) => {
	const fetchParams = {
		filters: params.filters,
		current: params.pagination?.current,
		pageSize: params.pagination?.pageSize,
		search: params.search,
		sorter: params.sorter,
	};
	return JSON.stringify({
		filters: fetchParams.filters,
		current: fetchParams.current,
		pageSize: fetchParams.pageSize,
		search: fetchParams.search,
		sorter: fetchParams.sorter,
	});
};

const CommandsGrid = (props) => {
	const {
		isLoading,
		data,
		grid,
		lastRequestParams,
		fetchCommands,
		setGridParams,
		resetGridData,
		deviceTypesTreeData,
		deviceTypesIdToLabelMap,
	} = props;

	const lastFetchedParamsRef = useRef(null);

	useEffect(() => {
		const serialized = serializeParams(grid.params);
		const lastSerialized = lastRequestParams ? serializeParams(lastRequestParams) : null;
		if (lastSerialized && serialized === lastSerialized) {
			lastFetchedParamsRef.current = serialized;
			return;
		}
		if (lastFetchedParamsRef.current === serialized) {
			return;
		}
		lastFetchedParamsRef.current = serialized;
		loadData(grid, fetchCommands);
	}, [
		grid.params.sorter,
		grid.params.filters,
		grid.params.pagination?.current,
		grid.params.pagination?.pageSize,
		grid.params.search,
		lastRequestParams,
	]);

	const handleTableChange = (pagination, filters, sorter) => {
		setGridParams({ ...grid.params, pagination, sorter });
	};

	return (
		<Container>
			<CommandsGridToolbar
				grid={grid}
				setGridParams={setGridParams}
				resetGridData={resetGridData}
				deviceTypesTreeData={deviceTypesTreeData}
				deviceTypesIdToLabelMap={deviceTypesIdToLabelMap}
			/>
			{!hasDeviceSerial(grid) && (
				<Alert
					type="info"
					showIcon
					style={{ margin: '8px 0' }}
					message="Select a Device Type and enter a Device Serial Number, then click Get Data to load commands."
				/>
			)}
			<Spin spinning={isLoading}>
				<Table
					dataSource={hasDeviceSerial(grid) ? (data || []) : []}
					columns={columns}
					rowKey={(record) => record.messageId || `${record.deviceType}-${record.timestamp}-${record.commandName}`}
					scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
					pagination={{
						...grid.params.pagination,
						showSizeChanger: true,
						showTotal: (total) => formatTotal(total, 'commands', grid.params.pagination),
					}}
					onChange={handleTableChange}
					size="small"
				/>
			</Spin>
		</Container>
	);
};

export default CommandsGrid;
